import React, { useState, useMemo, useCallback } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Edit,
  Trash2,
  Copy,
  Save,
  Search,
  Calendar,
  Clock,
  AlertCircle,
  Check,
  X
} from 'lucide-react';
import { DayShift, Employee } from '@/types';
import { format, isSameDay } from 'date-fns';
import { cn } from '@/lib/core/utils';
import { ShiftStatus } from '@/lib/scheduling/shift-status-types';
import EditableShiftCard from './EditableShiftCard';
import SaveShiftTemplateDialog from './SaveShiftTemplateDialog';
import { AdvancedShiftCopyDialog } from './AdvancedShiftCopyDialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useHistoricalEdit } from '@/context/HistoricalEditContext';
import { toast } from '@/hooks/ui/use-toast';

interface ShiftEditingModePanelProps {
  businessId: string;
  selectedDate?: Date;
  allDayShifts: DayShift[];
  employees: Employee[];
  selectedShiftIds: Set<string>;
  onShiftSelect: (shiftId: string, selected: boolean) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onEditShift: (shiftId: string) => void;
  onDeleteShift: (shiftId: string) => void;
  onCopyShifts: (shiftIds: string[]) => void;
  onSaveAsTemplate: (shiftIds: string[], name: string, description?: string) => void;
  onShiftCreated?: () => void;
  className?: string;
  // New prop to control filtering behavior
  isTemplatesEditingMode?: boolean;
}

const ShiftEditingModePanel: React.FC<ShiftEditingModePanelProps> = ({
  businessId,
  selectedDate,
  allDayShifts,
  employees,
  selectedShiftIds,
  onShiftSelect,
  onSelectAll,
  onClearSelection,
  onEditShift,
  onDeleteShift,
  onCopyShifts,
  onSaveAsTemplate,
  onShiftCreated,
  className = "",
  isTemplatesEditingMode = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<'bulk' | 'single' | null>(null);
  const [shiftToDelete, setShiftToDelete] = useState<string | null>(null);

  // Historical edit protection
  const { isShiftLocked } = useHistoricalEdit();

  // Only show selected shifts, filtered by search term if provided
  const filteredShifts = useMemo(() => {
    // Start with only selected shifts
    let shifts = allDayShifts.filter(shift => selectedShiftIds.has(shift.id));

    // Filter by search term if provided
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      shifts = shifts.filter(shift => {
        const employee = employees.find(emp => emp.id === shift.employee_id);
        const employeeName = employee?.name?.toLowerCase() || '';
        const startTime = format(new Date(shift.start_time), 'HH:mm');
        const endTime = format(new Date(shift.end_time), 'HH:mm');

        return (
          employeeName.includes(term) ||
          startTime.includes(term) ||
          endTime.includes(term) ||
          shift.id.toLowerCase().includes(term)
        );
      });
    }

    // Sort by start time
    return shifts.sort((a, b) =>
      new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );
  }, [allDayShifts, selectedShiftIds, searchTerm, employees]);

  // Get employee info for a shift
  const getEmployeeForShift = useCallback((shift: DayShift) => {
    return employees.find(emp => emp.id === shift.employee_id);
  }, [employees]);

  // Handle bulk actions
  const handleDeleteSelected = useCallback(() => {
    if (selectedShiftIds.size === 0) return;

    setDeleteTarget('bulk');
    setShowDeleteDialog(true);
  }, [selectedShiftIds]);

  const handleCopySelected = useCallback(() => {
    if (selectedShiftIds.size === 0) return;
    setShowCopyDialog(true);
  }, [selectedShiftIds]);

  const handleSaveSelectedAsTemplate = useCallback(() => {
    if (selectedShiftIds.size === 0) return;
    setShowSaveTemplateDialog(true);
  }, [selectedShiftIds]);

  const handleSaveTemplate = useCallback((name: string, description?: string) => {
    onSaveAsTemplate(Array.from(selectedShiftIds), name, description);
    setShowSaveTemplateDialog(false);
    onClearSelection();
  }, [selectedShiftIds, onSaveAsTemplate, onClearSelection]);

  const handleCopyShifts = useCallback(() => {
    onCopyShifts(Array.from(selectedShiftIds));
    setShowCopyDialog(false);
    onClearSelection();
  }, [selectedShiftIds, onCopyShifts, onClearSelection]);

  const handleDeleteSingle = useCallback((shiftId: string) => {
    setDeleteTarget('single');
    setShiftToDelete(shiftId);
    setShowDeleteDialog(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteTarget === 'bulk' && selectedShiftIds.size > 0) {
      // Filter out protected shifts
      const shiftsToDelete = Array.from(selectedShiftIds);
      const protectedShifts: string[] = [];
      const deletableShifts: string[] = [];

      shiftsToDelete.forEach(shiftId => {
        const shift = allDayShifts.find(s => s.id === shiftId);
        if (shift && isShiftLocked(shift.startTime, shift.isHistorical || false)) {
          protectedShifts.push(shiftId);
        } else {
          deletableShifts.push(shiftId);
        }
      });

      // Delete only non-protected shifts
      deletableShifts.forEach(shiftId => {
        onDeleteShift(shiftId);
      });

      // Show warning if some shifts were protected
      if (protectedShifts.length > 0) {
        toast.error(`${protectedShifts.length} shift(s) could not be deleted`, {
          description: 'Some shifts are protected. Enable "Editing Past" mode to delete historical shifts.'
        });
      }

      onClearSelection();
    } else if (deleteTarget === 'single' && shiftToDelete) {
      // Check if single shift is protected
      const shift = allDayShifts.find(s => s.id === shiftToDelete);
      if (shift && isShiftLocked(shift.startTime, shift.isHistorical || false)) {
        toast.error('Cannot delete protected shift', {
          description: 'Enable "Editing Past" mode to delete this shift.'
        });
      } else {
        onDeleteShift(shiftToDelete);
      }
    }

    setShowDeleteDialog(false);
    setDeleteTarget(null);
    setShiftToDelete(null);
  }, [deleteTarget, selectedShiftIds, shiftToDelete, onDeleteShift, onClearSelection, allDayShifts, isShiftLocked]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteDialog(false);
    setDeleteTarget(null);
    setShiftToDelete(null);
  }, []);

  const selectedShifts = useMemo(() => 
    filteredShifts.filter(shift => selectedShiftIds.has(shift.id)),
    [filteredShifts, selectedShiftIds]
  );

  return (
    <div className={`w-full h-full flex flex-col bg-transparent ${className}`}>
      {/* Header */}
      <div className="p-2 space-y-2 flex-shrink-0">
        {/* Search */}
        <div className="relative">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-6 pr-7 !bg-background"
            placeholder="Search..."
            aria-label="Search shifts"
          />
          <Search className="h-3 w-3 absolute right-2 top-1.5 text-muted-foreground" />
        </div>

        {/* Selection info and actions */}
        {selectedShiftIds.size > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <Badge className="text-xs px-1 py-0">
                {selectedShiftIds.size}
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                className="h-5 w-5 p-0"
                onClick={onClearSelection}
                title="Clear selection"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>

            {/* Bulk actions - icon only buttons */}
            <div className="flex gap-1 justify-center">
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={handleCopySelected}
                title="Copy selected shifts"
              >
                <Copy className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={handleSaveSelectedAsTemplate}
                title="Save selected shifts as template"
              >
                <Save className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                onClick={handleDeleteSelected}
                title="Delete selected shifts"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}

        {/* Clear selection button - only show when there are selected shifts */}
        {selectedShiftIds.size > 0 && (
          <div className="flex justify-between items-center text-xs">
            <Button
              size="sm"
              variant="ghost"
              className="h-5 px-1 text-xs flex-1 min-w-0"
              onClick={onClearSelection}
              title="Clear all selections"
            >
              <X className="h-3 w-3 mr-1" />
              <span className="truncate">Clear All</span>
            </Button>

            {selectedDate && (
              <div className="text-muted-foreground text-xs ml-1 flex-shrink-0">
                {format(selectedDate, 'MMM d')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Scrollable content */}
      <ScrollArea className="flex-1 px-2">
        {selectedShiftIds.size === 0 ? (
          <div className="text-xs text-muted-foreground py-8 text-center">
            <Calendar className="h-4 w-4 mx-auto mb-2 opacity-50" />
            <p className="mb-1">No shifts selected</p>
            <p className="text-xxs opacity-75">Click on shifts in the calendar to select them</p>
          </div>
        ) : filteredShifts.length === 0 ? (
          <div className="text-xs text-muted-foreground py-8 text-center">
            <Clock className="h-4 w-4 mx-auto mb-2 opacity-50" />
            <p>No selected shifts match your search</p>
          </div>
        ) : (
          <div className="space-y-1 pb-2">
            {filteredShifts.map(shift => (
              <EditableShiftCard
                key={shift.id}
                shift={shift}
                employee={getEmployeeForShift(shift)}
                selected={selectedShiftIds.has(shift.id)}
                onSelect={(selected) => onShiftSelect(shift.id, selected)}
                onEdit={() => onEditShift(shift.id)}
                onDelete={() => handleDeleteSingle(shift.id)}
                onCopy={() => onCopyShifts([shift.id])}
                onSaveAsTemplate={(name, description) => onSaveAsTemplate([shift.id], name, description)}
                status={shift.calculatedStatus || ShiftStatus.PENDING}
              />
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Dialogs */}
      <SaveShiftTemplateDialog
        isOpen={showSaveTemplateDialog}
        onClose={() => setShowSaveTemplateDialog(false)}
        onSave={handleSaveTemplate}
        selectedShifts={selectedShifts}
        employees={employees}
      />

      <AdvancedShiftCopyDialog
        open={showCopyDialog}
        onOpenChange={setShowCopyDialog}
        dayShifts={selectedShifts}
        selectedDate={selectedDate}
        businessId={businessId}
        onShiftCreated={onShiftCreated || (() => {})}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => {
        if (!open) handleCancelDelete();
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Delete Shift(s)</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget === 'bulk'
                ? `Are you sure you want to delete ${selectedShiftIds.size} selected shift(s)? This action cannot be undone.`
                : 'Are you sure you want to delete this shift? This action cannot be undone.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ShiftEditingModePanel;